import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, LinearProgress, Chip, Alert,
  CircularProgress, List, ListItem, ListItemText, Divider, Button, Paper
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import { dmsApi } from '../services/dmsApi';

const SCORE_CONFIG = (score) => {
  if (score >= 80) return { color: '#4caf50', label: 'Good', icon: <CheckCircleIcon /> };
  if (score >= 60) return { color: '#ff9800', label: 'Fair', icon: <WarningIcon /> };
  return { color: '#f44336', label: 'At Risk', icon: <ErrorIcon /> };
};

const VAULT_LABELS = {
  procedures: 'Procedures', legal: 'Legal', technical: 'Technical',
  financial: 'Financial', marketing: 'Marketing', contracts: 'Contracts',
  customer: 'Customer', hr: 'HR', compliance: 'Compliance',
};

export default function ComplianceDashboard({ onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await dmsApi.getComplianceDashboard();
        setData(res.data);
      } catch (err) {
        setError('Failed to load compliance data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;
  if (!data) return null;

  const overallConfig = SCORE_CONFIG(data.overallScore);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <SecurityIcon sx={{ fontSize: 32, color: overallConfig.color }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>Compliance Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">Monitor document governance across all vaults</Typography>
        </Box>
      </Box>

      {/* Overall Score */}
      <Paper sx={{
        p: 3, mb: 3, borderRadius: 3,
        background: `linear-gradient(135deg, ${overallConfig.color}15, ${overallConfig.color}05)`,
        border: `1px solid ${overallConfig.color}30`
      }}>
        <Grid container alignItems="center" spacing={3}>
          <Grid item xs={12} sm={4} sx={{ textAlign: 'center' }}>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <CircularProgress
                variant="determinate"
                value={data.overallScore}
                size={100}
                thickness={6}
                sx={{ color: overallConfig.color }}
              />
              <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                <Typography variant="h5" fontWeight={800} color={overallConfig.color}>{data.overallScore}</Typography>
              </Box>
            </Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Overall Score</Typography>
            <Chip label={overallConfig.label} size="small" sx={{ bgcolor: `${overallConfig.color}20`, color: overallConfig.color, fontWeight: 700, mt: 0.5 }} />
          </Grid>
          <Grid item xs={12} sm={8}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Issue Summary</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight={800} color="error.main">{data.summary?.criticalCount || 0}</Typography>
                <Typography variant="caption" color="text.secondary">Critical</Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight={800} color="warning.main">{data.summary?.mediumCount || 0}</Typography>
                <Typography variant="caption" color="text.secondary">Medium</Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight={800} color="info.main">{data.summary?.lowCount || 0}</Typography>
                <Typography variant="caption" color="text.secondary">Low</Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Vault Scores Heatmap */}
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Vault Compliance Scores</Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {(data.vaultScores || []).map(vault => {
          const cfg = SCORE_CONFIG(vault.score);
          return (
            <Grid item xs={12} sm={6} md={4} key={vault.vault}>
              <Card sx={{ borderLeft: `4px solid ${cfg.color}`, borderRadius: 2, bgcolor: `${cfg.color}08` }}>
                <CardContent sx={{ pb: '12px !important' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700}>{VAULT_LABELS[vault.vault] || vault.vault}</Typography>
                    <Chip label={`${vault.score}/100`} size="small" sx={{ bgcolor: `${cfg.color}20`, color: cfg.color, fontWeight: 700 }} />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={vault.score}
                    sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(0,0,0,0.1)', '& .MuiLinearProgress-bar': { bgcolor: cfg.color } }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {vault.documentCount} documents
                  </Typography>
                  {vault.issues && vault.issues.slice(0, 2).map((issue, idx) => (
                    <Alert key={idx} severity={issue.severity === 'high' ? 'error' : issue.severity === 'medium' ? 'warning' : 'info'}
                      icon={false} sx={{ mt: 0.5, py: 0, px: 1, '& .MuiAlert-message': { fontSize: '0.7rem', py: 0 } }}>
                      {issue.message}
                    </Alert>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Critical Issues */}
      {data.criticalIssues && data.criticalIssues.length > 0 && (
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: 'error.main' }}>🚨 Critical Issues</Typography>
          <List disablePadding>
            {data.criticalIssues.map((issue, idx) => (
              <React.Fragment key={idx}>
                <ListItem sx={{ bgcolor: 'error.dark', opacity: 0.9, borderRadius: 1, mb: 1 }}>
                  <ErrorIcon sx={{ mr: 1, color: 'error.light', fontSize: 20 }} />
                  <ListItemText
                    primary={<Typography variant="body2" fontWeight={600}>{issue.message}</Typography>}
                    secondary={<Typography variant="caption" color="text.secondary">Vault: {VAULT_LABELS[issue.vault] || issue.vault}</Typography>}
                  />
                  <Button size="small" variant="outlined" color="error" onClick={() => onNavigate && onNavigate('vault', issue.vault)}>
                    Review
                  </Button>
                </ListItem>
                {idx < data.criticalIssues.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
}
